import React, { memo, type ReactElement, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Loader, Typography } from '@saviynt/design-system';
import cx from 'classnames';

import {
  CF_NEU_BLD,
  ENTITLEMENT_INI_CALL,
  testIds,
  violationsEntitlement,
} from '../../../shared/constants';
import { useInvertedLocalize, useTypedSelector } from '../../../shared/hooks';
import { globalActions } from '../../../shared/store/actions';
import { createDevRejectionHandler } from '../../../shared/utils';
import {
  useEntitlementColumnPicker,
  useFiltersInitialization,
  useGetSortMenu,
} from '../hooks';
import usePageTracker from '../hooks/use-pagetracker';

import EntitlementControls from './EntitlementControls';
import EntitlementInsights from './EntitlementInsights';
import EntitlementSidePanel from './EntitlementSidePanel';
import EntitlementTabList from './EntitlementTabList';

import './EntitlementTab.css';

/**
 * ViolationsEntitlement Component
 *
 * Main container component for the Entitlements with Inherent Risk tab in the SOD violations feature.
 * Orchestrates initialization of filters, columns, and sorting while managing layout including
 * controls, list view, and sidepanel functionality with responsive design and loading states.
 *
 * @component
 * @returns {React.ReactElement} Main container with header, controls, list, and sidepanel including:
 *   - Title and navigation controls
 *   - Search, filters, sort, and column management
 *   - Infinite scroll entitlement list
 *   - Detailed sidepanel views
 *   - Loading states and error handling
 *
 * @example
 * <ViolationsEntitlement />
 *
 * @dependencies
 * - Redux: activeViolationsTab, isGlobalLoading, isSidePanelVisible from state.aag
 * - Hooks: useFiltersInitialization, useEntitlementColumnPicker, useGetSortMenu
 * - Components: EntitlementControls, EntitlementTabList, SodEntitlementSidePanel
 */
function ViolationsEntitlement(): ReactElement | null {
  const dispatch = useDispatch();
  const l = useInvertedLocalize();

  // Initial query hooks
  const { updateTracker } = (
    usePageTracker as (keys: string[]) => {
      updateTracker: (key: string, value: boolean) => void;
    }
  )(Object.values(ENTITLEMENT_INI_CALL));
  const { initializeAdvancedFilters } = (
    useFiltersInitialization as () => {
      initializeAdvancedFilters: () => Promise<boolean>;
    }
  )();
  const { initializeViewColumns } = (
    useEntitlementColumnPicker as () => {
      initializeViewColumns: () => Promise<boolean>;
    }
  )();

  const {
    user: { isMenuPinned },
    aag: {
      global: { activeViolationsTab, isGlobalLoading },
      entitlement: {
        controls: { sortInAction, isInsightsOpen },
        sidepanel: { isSidePanelVisible },
      },
    },
  } = useTypedSelector((state) => state);

  // Enable sort hook
  useGetSortMenu(sortInAction);

  // On page load
  useEffect(() => {
    if (activeViolationsTab === violationsEntitlement) {
      const initializeFilters = async () => {
        // Order is important
        const response: boolean[] = await Promise.all([
          initializeAdvancedFilters(),
          initializeViewColumns(),
        ]);

        // Raise global error is any one fails
        const isFalseValue = response.some((value) => !value);

        if (isFalseValue) {
          dispatch(globalActions.setGlobalError({ state: true }));
        }

        // Don't load page if untill all pass
        Object.values(ENTITLEMENT_INI_CALL).forEach((callValue, index) => {
          updateTracker(callValue, response[index]);
        });
      };

      initializeFilters().catch(
        createDevRejectionHandler('EntitlementTab: initializeFilters')
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only `activeViolationsTab` should trigger this
  }, [dispatch, activeViolationsTab]);

  const entitlementClasses = useMemo(
    () =>
      cx(
        'Sod-ViolationsEntitlement',
        isMenuPinned && 'Sod-ViolationsEntitlement--isMenuPinned'
      ),
    [isMenuPinned]
  );

  const listAsideClasses = useMemo(
    () =>
      cx(
        'Sod-ViolationsEntitlement-main',
        isSidePanelVisible && 'Sod-ViolationsEntitlement-main--isSidePanelOpen',
        isInsightsOpen && 'Sod-ViolationsEntitlement-main--isInsightsOpen',
        !isSidePanelVisible &&
          !isInsightsOpen &&
          'Sod-ViolationsEntitlement-main--isNotAsides'
      ),
    [isSidePanelVisible, isInsightsOpen]
  );

  const renderContent = useMemo(() => {
    if (isGlobalLoading) {
      return (
        <Box className='Sod-Violations-loaderWrapper'>
          <Loader kind='loop' format='main' color='brand' />
        </Box>
      );
    }

    return <EntitlementTabList />;
  }, [isGlobalLoading]);

  return (
    <Box
      className={entitlementClasses}
      dataTestId={testIds.SOD_ENTITLEMENT_TAB}>
      <Box tag='section' className={listAsideClasses}>
        {/* Controls */}
        <Box className='Sod-ViolationsEntitlement-listAndControls'>
          <Box className='Sod-ViolationsEntitlement-controlsWrapper'>
            {/* Tab Title */}
            <Box className='Sod-ViolationsEntitlement-listTitleWrapper'>
              <Typography kind='h2' color={CF_NEU_BLD}>
                {l('Entitlements with Inherent Risk')}
              </Typography>
            </Box>
            {/* Controls */}
            <EntitlementControls />
          </Box>
        </Box>
        {/* Main scroll area */}
        <Box className='Sod-ViolationsEntitlement-scrollArea'>
          {/* List */}
          {renderContent}
        </Box>
        {/* Entitlement Asides */}
        <Box className='Sod-ViolationsEntitlement-asides'>
          <EntitlementInsights className='Sod-ViolationsEntitlement-asidesInsight' />
          {!isInsightsOpen && isSidePanelVisible && <EntitlementSidePanel />}
        </Box>
      </Box>
    </Box>
  );
}

export default memo(ViolationsEntitlement);
